import { RRule, RRuleSet, rrulestr } from 'rrule';
import moment from 'moment-timezone';
import MomentTimezone from './zones';

// helper functions needed for locale offset
export const prevDay = day => (day === 0 ? 6 : day - 1);
export const nextDay = day => (day === 6 ? 0 : day + 1);

/**
 * Mutates date and sets UTC values
 */
export const utc = (date, time = []) => {
  if (!date.$utc) {
    date.setUTCSeconds(date.getTimezoneOffset() * 60);
    Object.defineProperty(date, '$utc', {
      value: true,
    });
  }

  if (time.length > 0 && !date.$time) {

    date.setHours(...time);
    Object.defineProperty(date, '$time', {
      value: true,
    });
  }

  return date;
};

export const parseTime = timeString =>
  moment(timeString, ['hh:mm a', 'h:mm a'], true);

  export const normalizeDate = (date, timeString, duration = 0, tz) => {
    // Parse the base date in the target timezone
    let wrappedDate = moment.tz(date, tz);
  
    // Extract hours and minutes from time string
    const time = moment(timeString, 'HH:mm'); // or 'hh:mm A' if you have AM/PM
    const hours = time.hours();
    const minutes = time.minutes();
  
    // Apply hours/minutes and duration
    wrappedDate = wrappedDate
      .hours(hours)
      .minutes(minutes)
      .seconds(0)
      .milliseconds(0)
      .add(duration, 'minutes');
  
    return wrappedDate;
  };
  

// serialisation helper objects
const rruleDays = {
  SU: RRule.SU,
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
};
const rruleDaysReverse = {
  SU: 'Sun',
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
};
const rruleDaysReplica = {
  Sun: 'SU',
  Mon: 'MO',
  Tue: 'TU',
  Wed: 'WE',
  Thu: 'TH',
  Fri: 'FR',
  Sat: 'SA',
};

const normalizeWeekdayCode = (day) => {
  if (!day) return null;
  if (rruleDays[day]) return day;
  return rruleDaysReplica[day] || null;
};

const frequencies = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
};

// deserialisation helper
const daysArray = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const freqArray = ['yearly', 'monthly', 'weekly', 'daily', 'hourly', 'minutely', 'secondly'];

export const toRruleString = ({
  recurring,
  recurringType,
  days,
  startDate,
  endDate,
  haslongduration,
  startTime,
  duration,
  tz,
  monthlyOption, // option to select when monthlt is selected for recurring events ['','']
  monthlyWeek, // week of the month e.g. 1,2,3,4,5
  monthlyweekDay, // week day of the selected week e.g sunday, monday, tuesday, wednesday
  monthlyDays, // day of the month for repeated events when "DayWise" is selected 
  repeatsEvery, // repeats in X months when selected monthly as option

}) => {
  // determing whether we need to have frequency or not
  const frequency = recurring && frequencies[recurringType];
  //console.log('frequency-- > ', recurringType)
  // normalize start date
  const dtstart = normalizeDate(startDate, startTime, 0, tz);
  const rruleOptions = {
    // must use cloned values, otherwise it will return original date
    dtstart: new Date(Date.UTC(dtstart.year(), dtstart.month(), dtstart.date(), 0, 0)),
    wkst: RRule.SU,
    byhour: dtstart.hours(),
    byminute: dtstart.minutes(),
    bysecond: 0,
    tzid: new MomentTimezone(tz),
  };
  if(days && days.length){
    days = days
      .map((day) => normalizeWeekdayCode(day))
      .filter(Boolean);
  }
   
  const byDay = days && days.length && days.map(day => rruleDays[day]);

  if (frequency) {
    rruleOptions.freq = frequency;
  }

  if (byDay && frequency == 2) {
    rruleOptions.byweekday = byDay;
  }

  if (!recurring) {
    rruleOptions.count = 1;
    rruleOptions.freq = RRule.DAILY;

  } else {

    if (recurringType === 'monthly') {
      rruleOptions.freq = RRule.MONTHLY;
      rruleOptions.interval = repeatsEvery || 1;

      if (monthlyOption === 'daywise') {
        rruleOptions.bymonthday = monthlyDays;
      } else {
        if (monthlyWeek && monthlyweekDay) {
          const weekdayCode = normalizeWeekdayCode(monthlyweekDay);
          rruleOptions.bysetpos = [monthlyWeek];
          if (weekdayCode) {
            rruleOptions.byweekday = [rruleDays[weekdayCode]];
          }
        }
      }
    }
    if (haslongduration) {
      endDate = moment(startDate);
      endDate = endDate.add(12, 'months'); //  adding 12 month in startDate
      // const until = normalizeDate(moment('9998-12-31T23:59:59'), startTime, duration, tz);
      // rruleOptions.until = until.toDate();

    } else {
      const until = normalizeDate(endDate, startTime, duration, tz);
      rruleOptions.until = until.toDate();
    }

  }
  //console.log('rruleOptions : ', rruleOptions)
  const rruleStr = new RRule(rruleOptions).toString();

  return rruleStr;
};

const compactTime = time => time.filter(unit => unit === 0 || Boolean(unit));

export const fromRruleString = (rruleString) => {
  try {


    const rrule = RRule.parseString(rruleString);
    //console.log('rrule===========> ', rrule)
    let eventData = {
      startDate: utc(rrule.dtstart, compactTime([rrule.byhour, rrule.byminute])),
      endDate: rrule.until,
      startTime: moment(rrule.dtstart).format('hh:mm a'),
    };

    if (rrule.freq) {
      eventData.recurring = true;
      eventData.recurringType = freqArray[rrule.freq];
    }

    if (rrule.byweekday && rrule.freq == 2) {
      eventData.days = rrule.byweekday.map(({ weekday }) => {
        return daysArray[weekday];
      });
    }

    if (rrule.count === 1) {
      eventData.recurring = false;
      eventData.recurringType = '';
      eventData.days = [];
    }

    if (rrule.freq === RRule.MONTHLY) {
      eventData.repeatsEvery = rrule.interval;
      if (rrule.bysetpos && rrule.byweekday) {
        eventData.monthlyOption = 'weekwise';
        eventData.monthlyWeek = rrule.bysetpos;
        eventData.monthlyweekDay = daysArray[rrule.byweekday[0].weekday]
      } else if (rrule.bymonthday) {
        eventData.monthlyOption = 'daywise';
        eventData.monthlyDays = rrule.bymonthday;
      }
    }
    return eventData;
  } catch (error) {
    console.error(error.message);
  }
};

export default toRruleString;
